import React, { useEffect, useMemo, useRef, useState } from "react";
import { TrophyIcon, XMarkIcon } from "@heroicons/react/24/outline";
import TableComponent from "../../components/TableComponent";
import ApiCustom from "../../utils/ApiCustom";
import { toast } from "react-toastify";

const JSON_HEADERS = { headers: { "Content-Type": "application/json" } };

// ---- permissions helpers ----
const isSuperAdmin = () => String(localStorage.getItem("userId")) === "1";
const getUserPermissions = () => {
  try {
    const raw = localStorage.getItem("permissions");
    return raw ? JSON.parse(raw).map((p) => p.permissionSlug) : [];
  } catch {
    return [];
  }
};
const hasPermission = (slug) =>
  isSuperAdmin() || getUserPermissions().includes(slug);

// ---- company helpers ----
const getRepresentUser = (company) =>
  company?.users?.find((u) => u?.CompaniesHasUsers?.isUserRepresentOfCompany);

/* ================= Modal chấm điểm (desktop-table + mobile-card) ================= */
const BatchScoreModal = ({
  open,
  company,
  onClose,
  onSaved,
  selectedYear,
  isUnpaid,
}) => {
  const canChange = hasPermission("POINT_SETTINGS");
  const representUser = getRepresentUser(company);
  const userId = representUser?.id;
  const companyId = company?.id;

  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [levelsMap, setLevelsMap] = useState({});
  const [serverPoints, setServerPoints] = useState({});
  const [serverNotes, setServerNotes] = useState({});
  const [selections, setSelections] = useState({});
  const noteTimers = useRef({});

  const isAuto = (c) => !!c.autoType;
  const manualRows = useMemo(
    () => categories.filter((c) => !c.autoType),
    [categories],
  );

  const setSelection = (cid, patch) =>
    setSelections((s) => ({ ...s, [cid]: { ...(s[cid] || {}), ...patch } }));

  const loadCategories = async () => {
    const res = await ApiCustom.get(`/point-categories`, {
      params: { page: 1, limit: 1000, s: "" },
    });
    const all = res.data?.data?.data || res.data?.data || [];
    const sorted =
      [...all].sort((a, b) => (a.order ?? 0) - (b.order ?? 0) || a.id - b.id) ||
      [];
    setCategories(sorted);

    const pairs = await Promise.all(
      sorted.map(async (c) => {
        const lvRes = await ApiCustom.get(`/point-levels`, {
          params: { page: 1, limit: 1000, pointCategoryId: c.id },
        });
        const arr = lvRes.data?.data?.data || lvRes.data?.data || [];
        const sortedLv =
          [...arr].sort(
            (x, y) => (x.order ?? 0) - (y.order ?? 0) || x.id - y.id,
          ) || [];
        return [c.id, sortedLv];
      }),
    );
    setLevelsMap(Object.fromEntries(pairs));
  };

  const loadPartnerPoints = async () => {
    if (!companyId) {
      setServerPoints({});
      setServerNotes({});
      return;
    }
    try {
      const res = await ApiCustom.get(`/partner-points/${companyId}`, {
        params: { year: selectedYear },
      });
      const payload = res.data?.data || {};
      const items = payload.items || [];
      const pts = {};
      const notes = {};
      items.forEach((it) => {
        if (it?.pointCategoryId == null) return;
        pts[it.pointCategoryId] = Number(it.points || 0);
        if (it.note != null) notes[it.pointCategoryId] = String(it.note);
      });
      setServerPoints(pts);
      setServerNotes(notes);
    } catch {
      setServerPoints({});
      setServerNotes({});
    }
  };

  const bootstrapSelections = () => {
    const next = {};
    for (const c of manualRows) {
      const cid = c.id;
      const currentPts = Number(serverPoints[cid] || 0);
      const lv =
        (levelsMap[cid] || []).find((x) => Number(x.points) === currentPts) ||
        null;
      next[cid] = { levelId: lv ? lv.id : null, note: serverNotes[cid] ?? "" };
    }
    setSelections(next);
  };

  // init
  useEffect(() => {
    if (!open || isUnpaid) return;
    (async () => {
      try {
        setLoading(true);
        await loadCategories();
        await loadPartnerPoints();
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, company?.id, selectedYear, isUnpaid]);

  // reset selections
  useEffect(() => {
    if (!open || isUnpaid) return;
    if (!manualRows.length) {
      setSelections({});
      return;
    }
    bootstrapSelections();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, manualRows.length, levelsMap, serverPoints, serverNotes, isUnpaid]);

  const rowPoint = (c) => {
    const cid = c.id;
    if (isAuto(c)) return Number(serverPoints[cid] || 0);
    const sel = selections[cid];
    if (sel?.levelId) {
      const lv = (levelsMap[cid] || []).find(
        (x) => String(x.id) === String(sel.levelId),
      );
      return Number(lv?.points || 0);
    }
    return Number(serverPoints[cid] || 0);
  };

  const onPickLevel = (cid, levelId) => setSelection(cid, { levelId });
  const onChangeNote = (cid, val) => {
    setSelection(cid, { note: val });
    if (noteTimers.current[cid]) clearTimeout(noteTimers.current[cid]);
    noteTimers.current[cid] = setTimeout(() => {}, 200);
  };

  const onSave = async () => {
    if (isUnpaid) {
      toast.error(`Hội viên chưa đóng hội phí năm ${selectedYear}.`);
      return;
    }
    if (!hasPermission("POINT_SETTINGS")) return;
    if (!userId || !companyId) {
      toast.error("Thiếu thông tin hội viên/đại diện");
      return;
    }

    const items = manualRows
      .map((c) => {
        const cid = c.id;
        const sel = selections[cid] || {};
        let levelId = sel.levelId || null;
        if (!levelId) {
          const curPts = Number(serverPoints[cid] || 0);
          const lv = (levelsMap[cid] || []).find(
            (x) => Number(x.points) === curPts,
          );
          if (lv) levelId = lv.id;
        }
        if (levelId) {
          return {
            categoryId: cid,
            levelId,
            note: (sel.note ?? "").trim() || null,
          };
        }
        return null;
      })
      .filter(Boolean);

    if (items.length === 0) {
      toast.error(
        "Hãy chọn ít nhất một mức điểm (hoặc giữ mức hiện tại để lưu ghi chú).",
      );
      return;
    }

    try {
      await ApiCustom.post(
        `/point-logs/bulk-upsert`,
        { userId, companyId, year: selectedYear, items },
        JSON_HEADERS,
      );
      toast.success("Đã lưu chấm điểm");
      onSaved && onSaved();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.message || "Lưu thất bại");
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center"
      onClick={onClose}
    >
      {/* max-w-8xl như yêu cầu */}
      <div
        className="relative bg-white w-full max-w-8xl mx-2 md:mx-12 rounded-2xl p-4 md:p-6 shadow-xl max-h-[92vh] md:max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-3 md:mb-4">
          <h3 className="text-base md:text-lg font-semibold">
            Chấm điểm · {company?.companyName || ""} · Năm {selectedYear}
          </h3>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {isUnpaid ? (
          <div className="border rounded-lg p-3 md:p-4 bg-amber-50 text-amber-700 text-sm md:text-base">
            Hội viên chưa đóng hội phí năm {selectedYear}. Không thể chấm điểm!
          </div>
        ) : loading ? (
          <div className="text-gray-500 p-3">Đang tải…</div>
        ) : (
          <>
            {/* Desktop (md+) = bảng */}
            <div className="hidden md:block flex-1 min-h-0 overflow-y-auto overflow-x-auto border rounded-lg">
              <table className="table w-full text-sm">
                <thead className="sticky top-0 bg-white">
                  <tr>
                    <th className="py-3">Tiêu chí</th>
                    <th className="py-3">Mức điểm</th>
                    {/* cột ghi chú có min-width để không bị bóp */}
                    <th className="py-3 w-1/3 min-w-[260px]">Ghi chú</th>
                  </tr>
                </thead>
                <tbody>
                  {categories.map((c) => {
                    const cid = c.id;
                    const levels = levelsMap[cid] || [];
                    const sel = selections[cid] || {};
                    const autoCurrentPts = Number(serverPoints[cid] || 0);
                    const autoCheckedId =
                      levels.find((lv) => Number(lv.points) === autoCurrentPts)
                        ?.id || null;

                    return (
                      <tr key={cid} className="align-top">
                        <td className="py-3">
                          <div className="font-medium flex flex-wrap items-start gap-1">
                            <span className="min-w-0 inline-block max-w-[220px] md:max-w-[420px] whitespace-normal break-words leading-snug">
                              {c.name}
                            </span>
                            {isAuto(c) && (
                              <span className="text-xs text-gray-500 italic mt-0.5 flex-none">
                                (Tiêu chí tự động chấm điểm)
                              </span>
                            )}
                          </div>

                          <div className="text-xs text-gray-500">
                            Mã: {c.key}
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            Điểm: <b>{rowPoint(c)}</b>
                          </div>
                        </td>

                        <td className="py-3">
                          {levels.length === 0 ? (
                            <span className="text-gray-400">
                              Chưa cài đặt cách tính điểm
                            </span>
                          ) : isAuto(c) ? (
                            <div className="flex flex-col gap-2">
                              {levels.map((lv) => (
                                <label
                                  key={lv.id}
                                  className="flex items-center gap-2 cursor-default"
                                >
                                  <input
                                    type="radio"
                                    name={`cate_${cid}`}
                                    className="radio"
                                    disabled
                                    checked={
                                      String(autoCheckedId || "") ===
                                      String(lv.id)
                                    }
                                    readOnly
                                  />
                                  <span className="text-sm">
                                    {lv.label} · <b>{lv.points}</b> điểm
                                    {lv.note ? (
                                      <span className="text-gray-500">
                                        {" "}
                                        — {lv.note}
                                      </span>
                                    ) : null}
                                  </span>
                                </label>
                              ))}
                            </div>
                          ) : (
                            <div className="flex flex-col gap-2">
                              {levels.map((lv) => (
                                <label
                                  key={lv.id}
                                  className="flex items-center gap-2"
                                >
                                  <input
                                    type="radio"
                                    name={`cate_${cid}`}
                                    className="radio"
                                    checked={
                                      String(sel.levelId || "") ===
                                      String(lv.id)
                                    }
                                    onChange={() => onPickLevel(cid, lv.id)}
                                  />
                                  <span className="text-sm">
                                    {lv.label} · <b>{lv.points}</b> điểm
                                    {lv.note ? (
                                      <span className="text-gray-500">
                                        {" "}
                                        — {lv.note}
                                      </span>
                                    ) : null}
                                  </span>
                                </label>
                              ))}
                            </div>
                          )}
                        </td>

                        {/* ô ghi chú: min-w để chống bóp, input cũng có min-w để dễ gõ */}
                        <td className="py-3 align-top min-w-[260px]">
                          {isAuto(c) || levels.length === 0 ? null : (
                            <input
                              className="input input-bordered w-full min-w-[240px]"
                              placeholder="Ghi chú"
                              value={sel.note ?? serverNotes[cid] ?? ""}
                              onChange={(e) =>
                                onChangeNote(cid, e.target.value)
                              }
                            />
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {categories.length === 0 && (
                    <tr>
                      <td colSpan={3} className="text-center text-gray-500">
                        Không có tiêu chí
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Mobile (<md) = giao diện điện thoại như bản OK trước đó */}
            <div className="md:hidden flex-1 min-h-0 overflow-y-auto space-y-3">
              {categories.length === 0 ? (
                <div className="text-center text-gray-500">
                  Không có tiêu chí
                </div>
              ) : (
                categories.map((c) => {
                  const cid = c.id;
                  const levels = levelsMap[cid] || [];
                  const sel = selections[cid] || {};
                  const autoCurrentPts = Number(serverPoints[cid] || 0);
                  const autoCheckedId =
                    levels.find((lv) => Number(lv.points) === autoCurrentPts)
                      ?.id || null;

                  return (
                    <div key={cid} className="border rounded-xl p-3">
                      <div className="font-medium text-sm">
                        {c.name}{" "}
                        {isAuto(c) && (
                          <span className="text-xs text-gray-500 italic">
                            (Tự động chấm điểm)
                          </span>
                        )}
                      </div>
                      <div className="text-[11px] text-gray-500">
                        Mã: {c.key} · Điểm: <b>{rowPoint(c)}</b>
                      </div>

                      <div className="mt-2">
                        {levels.length === 0 ? (
                          <span className="text-gray-400 text-sm">
                            Chưa cài đặt cách tính điểm
                          </span>
                        ) : isAuto(c) ? (
                          <div className="flex flex-col gap-1">
                            {levels.map((lv) => (
                              <label
                                key={lv.id}
                                className="flex items-center gap-2 cursor-default"
                              >
                                <input
                                  type="radio"
                                  name={`cate_${cid}`}
                                  className="radio radio-sm"
                                  disabled
                                  checked={
                                    String(autoCheckedId || "") ===
                                    String(lv.id)
                                  }
                                  readOnly
                                />
                                <span className="text-sm">
                                  {lv.label} · <b>{lv.points}</b> điểm
                                </span>
                              </label>
                            ))}
                          </div>
                        ) : (
                          <div className="flex flex-col gap-1">
                            {levels.map((lv) => (
                              <label
                                key={lv.id}
                                className="flex items-center gap-2"
                              >
                                <input
                                  type="radio"
                                  name={`cate_${cid}`}
                                  className="radio radio-sm"
                                  checked={
                                    String(sel.levelId || "") === String(lv.id)
                                  }
                                  onChange={() => onPickLevel(cid, lv.id)}
                                />
                                <span className="text-sm">
                                  {lv.label} · <b>{lv.points}</b> điểm
                                </span>
                              </label>
                            ))}
                          </div>
                        )}
                      </div>

                      {!isAuto(c) && levels.length > 0 ? (
                        <input
                          className="input input-bordered w-full mt-2"
                          placeholder="Ghi chú"
                          value={sel.note ?? serverNotes[cid] ?? ""}
                          onChange={(e) => onChangeNote(cid, e.target.value)}
                        />
                      ) : null}
                    </div>
                  );
                })
              )}
            </div>
          </>
        )}

        <div className="mt-4 md:mt-6 flex justify-end gap-2">
          <button className="btn btn-ghost" onClick={onClose}>
            Đóng
          </button>
          <button
            className="btn btn-primary"
            onClick={onSave}
            disabled={!canChange || isUnpaid}
          >
            Lưu
          </button>
        </div>
      </div>
    </div>
  );
};

/* ================= Trang chính: desktop-table + mobile-card ================= */
const ActivityScoring = () => {
  const [loading, setLoading] = useState(true);
  const [companies, setCompanies] = useState([]);
  const [search, setSearch] = useState("");

  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);

  const [scoreModal, setScoreModal] = useState({ open: false, company: null });
  const [totalsMap, setTotalsMap] = useState({});
  const [unpaidSet, setUnpaidSet] = useState(new Set());

  const loadCompanies = async () => {
    setLoading(true);
    try {
      const res = await ApiCustom.get("company-shows");
      setCompanies(res.data?.data?.data?.users || []);
    } catch {
      toast.error("Không tải được danh sách hội viên");
    } finally {
      setLoading(false);
    }
  };

  const loadTotalsForYear = async (list, y) => {
    try {
      const entries = await Promise.all(
        (list || []).map(async (c) => {
          try {
            const r = await ApiCustom.get(`/partner-points/${c.id}`, {
              params: { year: y },
            });
            const payload = r.data?.data || {};
            const total = Number(payload.totalPoints || 0);
            return [c.id, total];
          } catch {
            return [c.id, 0];
          }
        }),
      );
      setTotalsMap(Object.fromEntries(entries));
    } catch {
      setTotalsMap({});
    }
  };

  const loadUnpaidForYear = async (y) => {
    try {
      const r = await ApiCustom.get(`/membership-fee-shows`);
      const fees = r.data?.data?.data || [];
      const feesInYear = fees.filter((f) => {
        const t = new Date(f.createdAt || f.payStartDay || 0);
        return !isNaN(t) && t.getFullYear() === y;
      });
      if (!feesInYear.length) {
        setUnpaidSet(new Set());
        return;
      }
      const lists = await Promise.all(
        feesInYear.map((f) =>
          ApiCustom.get(`/membership-fee-not-paid/${f.id}`)
            .then((res) => res.data?.data?.data?.companies || [])
            .catch(() => []),
        ),
      );
      const ids = new Set();
      lists.flat().forEach((c) => c?.id && ids.add(c.id));
      setUnpaidSet(ids);
    } catch {
      setUnpaidSet(new Set());
    }
  };

  useEffect(() => {
    loadCompanies();
  }, []);
  useEffect(() => {
    if (companies.length) loadTotalsForYear(companies, year);
    else setTotalsMap({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companies, year]);
  useEffect(() => {
    loadUnpaidForYear(year);
  }, [year]);

  const filtered = useMemo(() => {
    if (!search.trim()) return companies;
    const q = search.toLowerCase();
    return companies.filter((item) =>
      [
        item.companyName,
        item.userFullName,
        item.userPhone,
        item.userEmail,
        item.approvalStatus,
      ].some((f) => f?.toLowerCase()?.includes(q)),
    );
  }, [companies, search]);

  /* Desktop headers */
  const headers = [
    {
      key: "id",
      label: "STT",
      render: (_, index) => {
        return <span>{index + 1}</span>;
      },
    },
    {
      key: "logo",
      label: "Logo",
      render: (item) => (
        <div className="w-12 h-12 md:w-16 md:h-16 rounded-lg overflow-hidden bg-white mx-auto">
          {item?.userAvatar ? (
            <img
              src={item.userAvatar}
              alt={item.companyName}
              className="w-full h-full object-contain"
              loading="lazy"
            />
          ) : (
            <img
              src={`https://ui-avatars.com/api/?name=${item?.companyName}&size=200&background=random&color=fff&bold=true`}
              alt="Profile"
              className="w-full h-full object-contain"
              loading="eager"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = `https://ui-avatars.com/api/?name=${item?.companyName}&size=200&background=random&color=fff&bold=true`;
              }}
            />
          )}
        </div>
      ),
    },
    {
      key: "companyName",
      label: "Đơn vị",
      render: (item) => {
        const rep =
          item?.users?.find(
            (u) => u?.CompaniesHasUsers?.isUserRepresentOfCompany,
          ) || null;
        return (
          <div className="whitespace-normal max-w-[600px] min-w-56 text-wrap">
            <div className="font-medium">{item.companyName}</div>
            <div className="text-xs text-gray-500">
              {rep?.userFullName || "—"}
            </div>
          </div>
        );
      },
    },
    {
      key: "contact",
      label: "Liên hệ",
      render: (item) => (
        <div className="text-sm whitespace-normal">
          <div>{item.userPhone}</div>
          <div className="text-gray-500 break-all">{item.userEmail}</div>
        </div>
      ),
    },
    {
      key: "yearPoints",
      label: `Điểm năm ${year}`,
      render: (item) => (
        <span
          className={`font-medium ${unpaidSet.has(item.id) ? "text-gray-400" : ""}`}
        >
          {unpaidSet.has(item.id) ? 0 : (totalsMap[item.id] ?? 0)}
        </span>
      ),
    },
  ];
  if (hasPermission("POINT_SETTINGS"))
    headers.push({ key: "setting", label: "Thao tác" });

  const tableSettings = {
    renderSettings: (item) =>
      hasPermission("POINT_SETTINGS") ? (
        <button
          className="btn btn-outline btn-warning btn-sm px-3 my-1 rounded"
          onClick={() => setScoreModal({ open: true, company: item })}
          title="Chấm điểm"
        >
          <TrophyIcon className="w-5 h-5" />
        </button>
      ) : null,
  };

  return (
    <div className="p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 md:gap-4 mb-4">
        <h1 className="text-xl md:text-2xl font-bold">Chấm điểm hoạt động</h1>

        <div className="flex flex-col sm:flex-row sm:items-center gap-2 md:gap-3 w-full md:w-auto">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">Năm:</span>
            <select
              className="select select-bordered select-sm md:select-md"
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
            >
              {Array.from(
                { length: 6 },
                (_, i) => new Date().getFullYear() - i,
              ).map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </div>

          <input
            type="search"
            className="input input-bordered w-full sm:w-72"
            placeholder="Tìm theo tên/điện thoại/email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {loading ? (
        <div className="text-gray-500">Đang tải…</div>
      ) : (
        <>
          {/* Desktop (md+): bảng */}
          <div className="hidden md:block overflow-x-auto">
            <TableComponent
              headers={headers}
              data={filtered}
              settings={tableSettings}
            />
          </div>

          {/* Mobile (<md): card list (giữ đúng giao diện đã OK) */}
          <div className="md:hidden space-y-3">
            {filtered.length === 0 && (
              <div className="text-gray-500">Không có hội viên phù hợp</div>
            )}
            {filtered.map((item) => {
              const rep =
                item?.users?.find(
                  (u) => u?.CompaniesHasUsers?.isUserRepresentOfCompany,
                ) || null;
              const total = unpaidSet.has(item.id)
                ? 0
                : (totalsMap[item.id] ?? 0);

              return (
                <div key={item.id} className="border rounded-xl p-3 flex gap-3">
                  <div className="w-14 h-14 rounded-lg overflow-hidden bg-white flex-shrink-0">
                    {item?.userAvatar ? (
                      <img
                        src={item.userAvatar}
                        alt={item.companyName}
                        className="w-full h-full object-contain"
                      />
                    ) : (
                      <img
                        src={`https://ui-avatars.com/api/?name=${item?.companyName}&size=200&background=random&color=fff&bold=true`}
                        alt="Profile"
                        className="w-full h-full object-contain"
                      />
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="font-medium leading-tight">
                      {item.companyName}
                    </div>
                    <div className="text-xs text-gray-500 truncate">
                      {rep?.userFullName || "—"}
                    </div>

                    <div className="mt-2 grid grid-cols-2 gap-2 text-sm">
                      <div className="text-gray-600">
                        <div className="text-[11px] uppercase tracking-wide">
                          Điểm {year}
                        </div>
                        <div
                          className={`font-medium ${unpaidSet.has(item.id) ? "text-gray-400" : ""}`}
                        >
                          {total}
                        </div>
                      </div>
                      <div>
                        {hasPermission("POINT_SETTINGS") && (
                          <button
                            className="btn btn-outline btn-warning btn-sm w-full"
                            onClick={() =>
                              setScoreModal({ open: true, company: item })
                            }
                          >
                            <TrophyIcon className="w-4 h-4 mr-1" />
                            Chấm điểm
                          </button>
                        )}
                      </div>
                    </div>

                    <div className="mt-2 text-xs text-gray-600">
                      <div>{item.userPhone}</div>
                      <div className="break-all text-gray-500">
                        {item.userEmail}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      <BatchScoreModal
        open={scoreModal.open}
        company={scoreModal.company}
        selectedYear={year}
        isUnpaid={
          scoreModal.company ? unpaidSet.has(scoreModal.company.id) : false
        }
        onClose={() => setScoreModal({ open: false, company: null })}
        onSaved={() => {
          const cid = scoreModal.company?.id;
          if (!cid) return;
          ApiCustom.get(`/partner-points/${cid}`, { params: { year } })
            .then((r) => {
              const payload = r.data?.data || {};
              const total = Number(payload.totalPoints || 0);
              setTotalsMap((m) => ({ ...m, [cid]: total }));
            })
            .catch(() => {});
        }}
      />
    </div>
  );
};

export default ActivityScoring;
